import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IAttendanceManagerItem, IStudentSummary, StudentRequest } from '@attendance/legacy-api/types';
import { HttpService } from '@nha/api/shared/classes';
import { isEmpty } from '@nha/functions/is-empty.function';
import { Observable } from 'rxjs';
import { AttendanceApiConfiguration } from '../attendance-api-configuration.class';
import { IAttendanceAuditRequest, IAttendanceManagerSummary } from '../types';
import { IAttendanceAuditEntry } from '../types/attendance-audit-entry.type';

@Injectable({
	providedIn: 'root'
})
export class ATMStudentAttendanceService extends HttpService {
	constructor() {
		const config = inject(AttendanceApiConfiguration);

		super(config, 'AttendanceManagementStudent');
	}

	getStudentAttendanceForDay(date: string, studentInSchoolIDs: number[]): Observable<IAttendanceManagerItem[]> {
		return this.post<IAttendanceManagerItem[]>(`${this.serviceUrl}/day/${date}`, studentInSchoolIDs);
	}

	getStudentAttendanceForWeek(startDate: string, endDate: string, studentInSchoolIDs: number[]): Observable<IAttendanceManagerSummary[]> {
		return this.post<IAttendanceManagerSummary[]>(`${this.serviceUrl}/week/${startDate}/${endDate}`, studentInSchoolIDs);
	}

	// Story 97808: audit trail (who entered/modified each record + when) for auditors. Keyed on the
	// logical slot (see IAttendanceAuditRequest) so the trail follows the slot across the new
	// AttendanceRecordIDs a clear/re-enter produces. academicYearID stays in the path so the read
	// hits one partition of the audit table.
	// No snackbarErrorMessage: this service is constructed without a snackbar service, so that option
	// is inert. A failed audit load is handled silently in the period cell (it just shows no history)
	// rather than popping a snackbar on a hover.
	getAttendanceRecordAudit(request: IAttendanceAuditRequest): Observable<IAttendanceAuditEntry[]> {
		return this.post<IAttendanceAuditEntry[]>(`${this.serviceUrl}/audit/${request.academicYearID}`, request);
	}

	getManagerGrid(studentRequest: StudentRequest): Observable<IStudentSummary[]> {
		const orderBy = studentRequest.gridSort.map((gs) => `${this.capitalizeFirstLetter(gs.sortColumn)}+${gs.sortOrder}`).join(',');

		const paramsObj = new HttpParams({
			fromObject: {
				academicYearId: studentRequest.academicYearId.toString(),
				schoolId: studentRequest.schoolId.toString(),
				startDate: studentRequest.startDate,
				endDate: studentRequest.endDate,
				$orderby: orderBy,
				includeInconsistencies: studentRequest.includeInconsistencies
			}
		});

		let paramsString = paramsObj.toString();

		if (!isEmpty(studentRequest.homeroomTeacherIds)) {
			paramsString += studentRequest.homeroomTeacherIds.map((ht) => `&homeroomTeacherIds=${ht}`).join('');
		}

		if (!isEmpty(studentRequest.teacherIds)) {
			paramsString += studentRequest.teacherIds.map((t) => `&teacherIds=${t}`).join('');
		}

		if (!isEmpty(studentRequest.attendanceCodeIds)) {
			paramsString += studentRequest.attendanceCodeIds.map((ac) => `&attendanceCodeIDs=${ac}`).join('');
		}

		if (studentRequest.attendanceInconsistency != null) {
			paramsString += `&attendanceInconsistency=${studentRequest.attendanceInconsistency}`;
		}

		if (studentRequest.learningModeIds != null) {
			paramsString += studentRequest.learningModeIds.map((lm) => `&learningModeIds=${lm}`).join('');
		}

		return this.http.get<IStudentSummary[]>(`${this.serviceUrl}`, {
			params: new HttpParams({ fromString: paramsString })
		});
	}

	private capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}
}
